import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from '../../atoms/Skeleton';
import './brandHeader.css';

/* BRAND PAGE HEADER — identity, then the products.

   The reference is `SP-main/css/storefront.css` l.890-917 (`shop-brand.html`
   itself is missing from the checkout), and it says:

       "THE PHOTO HERO IS GONE (2026-09-02 part 2). It was the single biggest
        reason the first product sat 838px down the page. The header is now
        identity only — mark, name, one line, facts — and the products start
        immediately under it."

   ⛔ So there are no tabs: crumb, this header, hairline, the browse surface. A
   tab row put the products behind a click on the page whose whole history is
   about getting them higher; the `Products (n)` / `Brand info` pair, its tablist
   and `?tab=` parameter are gone with it.

   The mark is 64px (`.sb-mark`), 52px under 767px — 104px is `.bb-logo`, the
   directory card's well. The hairline under the header is load-bearing, not
   decoration (Kendra, 2026-09-02), and lives on `.sb-head`'s own
   border-bottom. */

/* This is the page's <h1> and it contains only the name: an <h1> reading
   "VitaPet Supplements & dental care. Veterinarian-Founded · Made in USA" is the
   header block read aloud, not a heading. `Browse` renders an <h1> of its own;
   the page passes it no title and `brandHeader.css` hides the empty one. */
export default function BrandHead({
  name,
  logo,
  fallbackLogo,
  tagline,
  facts,
  loading,
}) {
  if (loading) return <BrandHeadSkeleton />;

  return (
    <header className="sb-head">
      <span className="sb-mark">
        <img
          src={logo || fallbackLogo}
          alt=""
          onError={(e) => { e.target.src = fallbackLogo; }}
        />
      </span>

      <div className="sb-id">
        <h1 className="sb-name">{name}</h1>

        {tagline && <p className="sb-line">{tagline}</p>}

        {/* The facts are plain text, never chips: "A chip is a selectable
            option; these are stored values, and as pills they read as filters"
            (storefront.css l.910). The separators stay muted and each fact is a
            <span> stepping up to --ink at 500. */}
        {facts?.length > 0 && (
          <p className="sb-facts">
            {facts.map((f, i) => (
              <React.Fragment key={f}>
                {i > 0 && ' · '}
                <span>{f}</span>
              </React.Fragment>
            ))}
          </p>
        )}
      </div>
    </header>
  );
}

/* The same block at the same heights, so the page does not jump when the brand
   lands. The mark is the real 64px well. */
export function BrandHeadSkeleton() {
  return (
    <header className="sb-head" aria-hidden="true">
      <span className="sb-mark"><Skeleton w={64} h={64} shape="circle" /></span>
      <div className="sb-id">
        <Skeleton w={220} h={34} />
        <div style={{ marginTop: 8 }}><Skeleton w={280} h={16} /></div>
        <div style={{ marginTop: 12 }}><Skeleton w={340} h={13} /></div>
      </div>
    </header>
  );
}

/* The story sits under the products, in the results column, and is absent when
   there is none (storefront.css l.914). It is passed to `Browse` as `belowGrid`,
   which renders exactly there. The heading is "About <brand>", the reference's
   own vocabulary for this content. */
export function BrandStory({ name, story }) {
  if (!story) return null;

  /* The reference's `.sb-why p` carries a bottom margin, i.e. it expects more
     than one paragraph. A partner's story arrives as one string, so blank lines
     stay paragraph breaks instead of collapsing into a wall. */
  const paras = String(story).split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);

  return (
    <section className="sb-why">
      <h2>{name ? `About ${name}` : 'About the brand'}</h2>
      {paras.map((p, i) => <p key={i}>{p}</p>)}
    </section>
  );
}

BrandHead.propTypes = {
  name: PropTypes.string,
  logo: PropTypes.string,
  fallbackLogo: PropTypes.string,
  tagline: PropTypes.string,
  facts: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
};

BrandStory.propTypes = {
  name: PropTypes.string,
  story: PropTypes.string,
};
